/* eslint-disable no-loop-func */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../../context/OnboardingContext';
import OnboardingLayout, { CancelButton } from './OnboardingLayout';
import { auth } from '../../config/firebase.config';
import { signInAnonymously } from 'firebase/auth';
import FirebaseStorageService from '../../services/firebase-storage.service';
import { v4 as uuidv4 } from 'uuid';

const CompanyHistory: React.FC = () => {
  const navigate = useNavigate();
  const { companyData, updateCompanyData, setCurrentStep } = useOnboarding();

  // Estado para la descripción de la empresa
  const [companyDescription, setCompanyDescription] = useState(companyData.companyDescription || '');

  // Estado para las imágenes de servicios (ahora con estructura para manejar URLs y previews)
  const [serviceImages, setServiceImages] = useState<Array<{
    id: string;
    preview: string;
    url?: string;
    file?: File;
    uploaded: boolean;
  }>>(companyData.serviceImages?.map((url: string) => ({
    id: uuidv4(),
    preview: url,
    url: url,
    uploaded: true
  })) || []);

  // Estado para mostrar si se ha alcanzado el límite de imágenes
  const [isImageLimitReached, setIsImageLimitReached] = useState(false);

  // Estado para rastrear si hay imágenes en proceso de carga
  const [isUploading, setIsUploading] = useState(false);

  // Verificar si la categoría está seleccionada, si no, redirigir
  useEffect(() => {
    if (!companyData.category) {
      navigate('/onboarding/category');
    }
  }, [companyData.category, navigate]);

  // Verificar si se ha alcanzado el límite de imágenes
  useEffect(() => {
    setIsImageLimitReached(serviceImages.length >= 20);
  }, [serviceImages]);

  // Manejar cambios en la descripción
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCompanyDescription(e.target.value);
  };

  // Manejar la subida de imágenes a Firebase Storage
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && serviceImages.length < 20) {
      const remainingSlots = 20 - serviceImages.length;
      const filesToProcess = Array.from(e.target.files).slice(0, remainingSlots);

      // Activar el estado de carga
      setIsUploading(true);

      // Verificar si hay un usuario autenticado, si no, crear uno anónimo
      const currentUser = auth.currentUser;
      if (!currentUser) {
        try {
          console.log('No hay usuario autenticado para subir imagen adicional, creando usuario anónimo...');
          await signInAnonymously(auth);
          console.log('Usuario anónimo creado exitosamente');
        } catch (error) {
          console.error('Error al crear usuario anónimo:', error);
          setIsUploading(false); // Desactivar estado de carga si hay error
          return; // No continuar si no se pudo crear el usuario anónimo
        }
      }

      // Contador para rastrear las imágenes completadas
      let completedUploads = 0;
      const totalUploads = filesToProcess.filter(file => file.type.startsWith('image/')).length;

      for (const file of filesToProcess) {
        if (file.type.startsWith('image/')) {
          // Crear un objeto temporal con preview local
          const imageId = uuidv4();
          const reader = new FileReader();

          reader.onloadend = () => {
            // Añadir imagen con preview local primero
            setServiceImages(prev => [...prev, {
              id: imageId,
              preview: reader.result as string,
              file: file,
              uploaded: false
            }]);

            // Subir a Firebase Storage
            FirebaseStorageService.uploadFile(file, 'service-images')
              .then(url => {
                // Actualizar la imagen con la URL de Firebase
                setServiceImages(prev => prev.map(img =>
                  img.id === imageId ? { ...img, url, uploaded: true } : img
                ));
                console.log('Imagen adicional subida exitosamente:', url);

                // Verificar si todas las imágenes se han completado
                completedUploads++;
                if (completedUploads === totalUploads) {
                  setIsUploading(false);
                }
              })
              .catch(error => {
                console.error('Error al subir imagen adicional:', error);
                // Marcar como error pero mantener la preview
                setServiceImages(prev => prev.map(img =>
                  img.id === imageId ? { ...img, uploaded: false } : img
                ));

                // Verificar si todas las imágenes se han completado (incluso con error)
                completedUploads++;
                if (completedUploads === totalUploads) {
                  setIsUploading(false);
                }
              });
          };

          reader.readAsDataURL(file);
        }
      }
    }
  };

  // Eliminar una imagen
  const handleRemoveImage = (id: string) => {
    setServiceImages(prev => prev.filter(img => img.id !== id));
  };

  // Verificar si el formulario es válido y no hay imágenes cargándose
  const isFormValid = companyDescription.trim().length > 0 && !isUploading;

  const handleBack = () => {
    setCurrentStep(4);
    navigate('/onboarding/company-details');
  };

  // Manejar el envío del formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid) return;

    // Verificar que todas las imágenes se hayan subido correctamente
    const allImagesUploaded = serviceImages.every(img => img.uploaded);

    if (!allImagesUploaded) {
      alert('Por favor espera a que todas las imágenes se suban correctamente');
      return;
    }

    // Extraer solo las URLs para guardar en el contexto
    const imageUrls = serviceImages.map(img => img.url as string);

    // Actualizar los datos en el contexto
    updateCompanyData({
      companyDescription,
      serviceImages: imageUrls
    });

    setCurrentStep(6); // Avanzar al siguiente paso
    navigate('/onboarding/locations'); // Navegar a la página de selección de ubicaciones
  };

  return (
    <OnboardingLayout
      title="¡Cuéntanos tu historia y muéstranos tu esencia!"
      subtitle="Este es el espacio para que compartas quién eres, cómo nació tu empresa y por qué haces lo que haces. Sube imágenes que reflejen la calidad de tus servicios y el amor por tu trabajo."
      showBackButton={true}
      onBack={handleBack}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Descripción de la empresa */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-xervix-text-primary dark:text-xervix-text-primary-dark">
              Descripción de la empresa
            </label>
            <textarea
              value={companyDescription}
              onChange={handleDescriptionChange}
              className="text-xervix-text-primary dark:text-xervix-text-primary-dark w-full px-4 py-3 rounded-lg bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark border border-xervix-border dark:border-xervix-border-dark focus:outline-none focus:ring-2 focus:ring-xervix-primary"
              placeholder="Cuéntanos la historia de tu empresa, sus valores y lo que la hace especial..."
              rows={8}
            />
          </div>

          {/* Imágenes de servicios */}
          <div className="space-y-2">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-xervix-text-primary dark:text-xervix-text-primary-dark">
                Sube las imágenes de los servicios que has realizado.
              </label>
              <span className="text-xs text-xervix-text-secondary dark:text-xervix-text-secondary-dark">
                {serviceImages.length}/20
              </span>
            </div>

            {/* Contenedor de imágenes */}
            <div className="bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark rounded-lg p-4 h-[200px] overflow-y-auto">
              {serviceImages.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {serviceImages.map((image, index) => (
                    <div key={image.id} className="relative group">
                      <img
                        src={image.preview}
                        alt={`Servicio ${index + 1}`}
                        className="w-full h-[74px] object-cover rounded-md"
                      />
                      {!image.uploaded && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                          <div className="loader w-5 h-5 border-2 border-t-2 border-white rounded-full animate-spin"></div>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(image.id)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center">
                  <div className="w-16 h-16 bg-xervix-bg-secondary dark:bg-xervix-bg-secondary-dark rounded-lg flex items-center justify-center mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-xervix-text-secondary dark:text-xervix-text-secondary-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <p className="text-center text-xervix-text-secondary dark:text-xervix-text-secondary-dark">
                    Puedes subir hasta 20 imágenes
                  </p>
                </div>
              )}
            </div>

            {/* Botón para subir imágenes */}
            <div className="flex justify-end mt-1">
              <label className={`flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium ${isImageLimitReached
                  ? ' text-xervix-text-secondary dark:text-xervix-text-secondary-dark cursor-not-allowed'
                  : ' text-xervix-text-secondary dark:text-xervix-text-secondary-dark hover:bg-opacity-90 cursor-pointer'
                } transition-colors`}>
                <span>{isImageLimitReached ? 'Límite de imágenes alcanzado' : 'Subir imágenes'}</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  multiple
                  disabled={isImageLimitReached}
                />
              </label>
            </div>
          </div>
        </div>

        <div className="flex justify-center gap-4 mt-8">
          <CancelButton />
          <button
            type="submit"
            disabled={!isFormValid}
            className={`px-8 py-3 rounded-md text-white font-medium transition-colors ${isFormValid
                ? 'bg-xervix-primary text-white hover:bg-opacity-90'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
          >
            {isUploading ? (
              <div className="flex items-center justify-center">
                <div className="loader w-4 h-4 border-2 border-t-2 border-white rounded-full animate-spin mr-2"></div>
                <span>Subiendo imágenes...</span>
              </div>
            ) : 'Continuar'}
          </button>
        </div>
      </form>
    </OnboardingLayout>
  );
};

export default CompanyHistory;
